import { PAYMENT_METHODS } from './constants';
import { PaymentMethodCardConfig } from './PaymentMethodOptionCard';
import { completePayment } from './apis';
import { dealerPayment } from 'utils/api/services';

export const getPaymentMethodConfig = (
    formattedOrderAmount: string | undefined
): Record<string, PaymentMethodCardConfig> => ({
    [PAYMENT_METHODS.WALLET]: {
        icon: '/icons/wallet.svg',
        optionTitle: 'Debit from Wallet',
        successText: `₹${formattedOrderAmount} will be debited from wallet`,
    },
    [PAYMENT_METHODS.PAYMENT_GATEWAY]: {
        icon: '/icons/card.svg',
        optionTitle: 'Use Payment Gateway',
        successText: 'Pay using Credit/Debit Card, UPI, or Net Banking',
    },
});

type CompletePaymentHandlerArgs = {
    selectedPaymentMethod: string;
    orderEkey: string;
    partnerId: string;
    setIsBtnLoading: (loading: boolean) => void;
    setSnackbar: (snackbar: { type: string; message: string }) => void;
    redirectToLogin: () => void;
    redirectToThankYou: (paymentOrderEkey: string) => void;
    redirectToGateway: (
        paymentGatewayUrl: string,
        paymentOrderEkey: string
    ) => void;
};

export const handleCompletePayment = async ({
    selectedPaymentMethod,
    orderEkey,
    partnerId,
    setIsBtnLoading,
    setSnackbar,
    redirectToLogin,
    redirectToThankYou,
    redirectToGateway,
}: CompletePaymentHandlerArgs) => {
    setIsBtnLoading(true);
    if (!orderEkey || !partnerId) {
        setIsBtnLoading(false);
        setSnackbar({
            type: 'failure',
            message: 'Unable to complete payment. Please try again.',
        });
        return;
    }

    if (selectedPaymentMethod === PAYMENT_METHODS.WALLET) {
        const { status, statusCode, data } = await completePayment(
            partnerId,
            orderEkey
        );

        if (statusCode === 401) {
            redirectToLogin();
            setSnackbar({
                type: 'failure',
                message: 'Token expired. Redirecting to login page.',
            });
            setIsBtnLoading(false);
            return;
        }

        if (status) {
            redirectToThankYou(data?.payment_order_ekey);
            return;
        }

        setIsBtnLoading(false);
        setSnackbar({
            type: 'failure',
            message: 'Unable to complete payment. Please try again.',
        });
        return;
    }

    if (selectedPaymentMethod === PAYMENT_METHODS.PAYMENT_GATEWAY) {
        if (!orderEkey) {
            setIsBtnLoading(false);
            setSnackbar({
                type: 'failure',
                message: 'Unable to complete payment. Please try again.',
            });
            return;
        }
        const { data, status, statusCode } = await dealerPayment(
            partnerId,
            String(orderEkey)
        );
        if (statusCode === 401) {
            redirectToLogin();
            setSnackbar({
                type: 'failure',
                message: 'Token expired. Redirecting to login page.',
            });
            return;
        }
        if (status && data?.payment_gateway_url) {
            redirectToGateway(
                String(data.payment_gateway_url),
                String(data?.payment_order_ekey || '')
            );
            return;
        }
        setSnackbar({
            type: 'failure',
            message: 'Oops. Something went wrong.',
        });
        setIsBtnLoading(false);
    }
};
